import React from "react";
import Dropzone from "react-dropzone";
import { FaTrash } from "react-icons/fa";
import styled from "styled-components";

import { IFileUploadResponseData } from "../../../../../api/CourseAuthorApi";
import { AxiosInterceptedResult } from "../../../../../api/interceptors";
import { Input } from "../../../../../App/Common/Components/FormComponents";

import {
  ImageDropDownMessage,
  OutlinedBox,
  PartImageDropZone,
} from "../../../../course-author/instruction/parts/image/ImagePart.styles";
import { config } from "../../../../../config";
import { ActionButton } from "../../ActionConfirmation";
import { Row } from "../../PreviewTab/UnitContentDropdown/Grid";

interface BackgroundOptionsProps {
  uploadFile: (file: File) => Promise<AxiosInterceptedResult<IFileUploadResponseData[]>>;
  patchBackgroundPart: (imageId: Nullable<string>) => void;
  imageId: Nullable<string>;
}

export const BackgroundOptions: React.FC<BackgroundOptionsProps> = props => {
  const [uploadedImage, setUploadedImage] = React.useState<Nullable<string>>(props.imageId);

  const mkImageUrl = (imgId: string): string => `${config.saltcomplianceApiRoot}/images/${imgId}/_download`;

  const handleImageUpload = (files: File[] | undefined) => {
    props.uploadFile(files![0]).then(([error, response]) => {
      if (error) return alert("Error uploading image");

      const imageId = response!.data[0].mediaId.slice(4);
      setUploadedImage(imageId);
      props.patchBackgroundPart(imageId);
    });
  };

  const deleteImage = () => {
    if (!uploadedImage) return;

    props.patchBackgroundPart(null);
    setUploadedImage(null);
  };

  return (
    <BackgroundOptionsWrapper>
      <Row>
        Background Image
        <ActionButton onClick={deleteImage} aria-label="delete">
          <FaTrash />
        </ActionButton>
      </Row>
      <UploadWrapper>
        <Dropzone onDrop={handleImageUpload}>
          {({ getRootProps, getInputProps }) => (
            <div {...getRootProps()} style={{ width: "100%" }}>
              <PartImageDropZone>
                <OutlinedBox backgroundImage={uploadedImage ? mkImageUrl(uploadedImage) : undefined}>
                  <ImageDropDownMessage>Drop the file here or click to browse and upload</ImageDropDownMessage>
                </OutlinedBox>
              </PartImageDropZone>
              <Input
                {...getInputProps()}
                data-testid="image-uploader"
                type="file"
                name="file"
                style={{ display: "none" }}
              />
            </div>
          )}
        </Dropzone>
      </UploadWrapper>
    </BackgroundOptionsWrapper>
  );
};

interface IBackgroundPreviewBoxProps {
  backgroundImage?: Nullable<string>;
}

export const BackgroundPreviewBox = styled.div<IBackgroundPreviewBoxProps>`
  background-color: #fff;
  border-radius: 4px;
  padding: 0 1rem;
  background-image: url(${props => props.backgroundImage ?? null});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

const BackgroundOptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 1px solid ${props => props.theme.colors.grey};
  border-radius: 5px;
  padding: 1rem;
  gap: 1rem;
`;

const UploadWrapper = styled.div`
  width: 35em;
`;
